/**
 * 字幕管理API
 */
import type { AxiosRequestConfig } from 'axios'
import { request } from '@/shared/api/request'
import type { BatchRequestConfig } from '@/shared/api/use-api-batch'

type RequestConfig = AxiosRequestConfig | BatchRequestConfig

// 字幕相关接口
export interface Subtitle {
  id: number
  work_item_id?: number
  /** @deprecated 语义为 work_item_id */
  project_id: number
  start_time: number
  end_time: number
  duration?: number
  original_text?: string
  translated_text?: string
  translated_text_multiline?: string
  language: string
  order_index: number
  is_edited: boolean
  modification_type: string
  created_at: string
  updated_at: string
}

export interface SubtitleCreate {
  start_time: number
  end_time: number
  original_text?: string
  translated_text?: string
  translated_text_multiline?: string
  language: string
  order_index: number
}

export interface SubtitleUpdate {
  start_time?: number
  end_time?: number
  original_text?: string
  translated_text?: string
  translated_text_multiline?: string
  order_index?: number
}

export interface SubtitleListResponse {
  items: Subtitle[]
  total: number
  work_item_id?: number
  /** @deprecated 语义为 work_item_id */
  project_id: number
  language?: string
}

export interface SubtitleBatchOperation {
  operation: 'replace' | 'delete' | 'adjust_timing'
  subtitle_ids?: number[]
  search_text?: string
  replace_text?: string
  language?: string
  time_offset?: number
}

export interface CharacterCountResponse {
  work_item_id?: number
  /** @deprecated 语义为 work_item_id */
  project_id: number
  language?: string
  original_character_count: number
  translated_character_count: number
  subtitle_count: number
  total_duration: number
  last_updated: string
}

// 字幕管理API
export const subtitleApi = {
  // 获取条目字幕列表（路径参数为 work_item_id）
  getProjectSubtitles: (workItemId: number, language?: string, config?: RequestConfig) =>
    request.get<Subtitle[]>(`/videos/work-items/${workItemId}/subtitles`, {
      params: { language },
      ...config,
    }),

  // 创建字幕（后端路径仍为 /subtitles/projects/{id}，id 语义为 work_item_id）
  createSubtitle: (workItemId: number, data: Partial<SubtitleCreate>) =>
    request.post<Subtitle>(`/subtitles/projects/${workItemId}/subtitles`, data),

  // 更新字幕
  updateSubtitle: (subtitleId: number, data: SubtitleUpdate) =>
    request.put<Subtitle>(`/subtitles/${subtitleId}`, data),

  // 删除字幕
  deleteSubtitle: (subtitleId: number) =>
    request.delete(`/subtitles/${subtitleId}`),

  // 拆分字幕（单条拆分）
  splitSubtitle: (subtitleId: number, splitTime: number, firstText?: string, secondText?: string) =>
    request.post<Subtitle[]>(`/subtitles/${subtitleId}/split`, {
      split_time: splitTime,
      first_text: firstText,
      second_text: secondText
    }),

  // 多条拆分字幕
  splitSubtitleMultiple: (subtitleId: number, firstText: string, splitPoints: Array<{ time: number; text: string }>) =>
    request.post<Subtitle[]>(`/subtitles/${subtitleId}/split-multiple`, {
      first_text: firstText,
      split_points: splitPoints
    }),

  // 合并字幕
  mergeSubtitles: (subtitleIds: number[]) =>
    request.post<Subtitle>(`/subtitles/merge?subtitle_ids=${subtitleIds.join(',')}`),

  // 批量更新字幕
  batchUpdateSubtitles: (workItemId: number, updates: any[]) =>
    request.post(`/subtitles/projects/${workItemId}/subtitles/batch-update`, {
      subtitle_updates: updates
    }),

  batchOperation: (workItemId: number, operation: SubtitleBatchOperation) =>
    request.post(`/subtitles/projects/${workItemId}/subtitles/batch-operation`, operation),

  getCharacterCount: (workItemId: number, language?: string) =>
    request.get<CharacterCountResponse>(`/subtitles/projects/${workItemId}/character-count`, {
      params: { language }
    }),

  saveProject: (workItemId: number) =>
    request.post(`/subtitles/projects/${workItemId}/save`),

  // 复制字幕
  duplicateSubtitle: (subtitleId: number) =>
    request.post<Subtitle>(`/subtitles/${subtitleId}/duplicate`),

  // 调整字幕时间
  adjustSubtitleTiming: (workItemId: number, timeOffset: number, subtitleIds?: number[]) =>
    request.post(`/subtitles/projects/${workItemId}/adjust-timing`, {
      time_offset: timeOffset,
      subtitle_ids: subtitleIds
    }),

  getSubtitleTimeline: (workItemId: number) =>
    request.get(`/subtitles/projects/${workItemId}/timeline`),

  updateProjectSubtitles: (workItemId: number, subtitles: any[]) =>
    request.post(`/subtitles/projects/${workItemId}/subtitles/batch-update`, {
      subtitle_updates: subtitles
    }),

  autoGenerateSubtitles: (workItemId: number, language?: string) =>
    request.post(`/videos/work-items/${workItemId}/auto-generate-subtitles`, null, {
      params: { language },
    }),

  getExtractionProgress: (workItemId: number) =>
    request.get(`/videos/work-items/${workItemId}/extraction-progress`),

  cancelExtraction: (workItemId: number) =>
    request.post(`/videos/work-items/${workItemId}/cancel-extraction`)
}

export default subtitleApi

